import { usePage } from "@inertiajs/react"
import type { ReactNode } from "react"

import { AssistantPanel } from "@/components/assistant/assistant-panel"
import { AssistantProvider } from "@/components/assistant/assistant-provider"
import { ClaimOutcomeModal } from "@/components/briefs/claim-outcome-modal"
import { HelpProvider } from "@/components/help/help-provider"
import { TourProvider } from "@/components/tours/tour-provider"
import { CommandPalette } from "@/components/search/command-palette"
import { ShortcutsDialog } from "@/components/shortcuts-dialog"
import { ErrorBoundary } from "@/components/error-boundary"
import { Toaster } from "@/components/ui/sonner"
import { type Appearance, useAppearance } from "@/hooks/use-appearance"
import { useFlash } from "@/hooks/use-flash"
import type { SharedData } from "@/types"

interface PersistentLayoutProps {
  children: ReactNode
}

/**
 * Applies the server's theme preference on every page load.
 *
 * This has to live here rather than in the user menu: the menu's content only
 * mounts when the dropdown is opened, so a preference set on another device
 * would not take effect until you happened to open the menu.
 */
function AppearanceSync() {
  const { auth } = usePage<SharedData>().props
  useAppearance(auth?.user?.appearance as Appearance | undefined)
  return null
}

export default function PersistentLayout({ children }: PersistentLayoutProps) {
  useFlash()
  return (
    <AssistantProvider>
      {/*
        Contextual help (INCO-386). Mounted here, beside the assistant panel and
        the command palette, for the same reason they are: the sheet has to
        survive Inertia navigation, and any surface has to be able to open it
        without threading a prop down through the page.
      */}
      <HelpProvider>
      {/*
        Guided tours (INCO-387). Inside HelpProvider because a tour step can
        open the article behind it, and mounted here so a tour survives the
        Inertia navigation between the screens it walks you through.
      */}
      <TourProvider>
      <AppearanceSync />
      <div className="flex min-h-screen w-full">
        <div className="flex min-w-0 flex-1 flex-col">
          <ErrorBoundary>{children}</ErrorBoundary>
        </div>
        <AssistantPanel />
      </div>
      <CommandPalette />
      <ShortcutsDialog />
      {/*
        What came of a brief that came through signup (INCO-470). Here rather
        than on the dashboard: the desk form redirects to the desk root, and
        whichever page that resolves to is the one the answer belongs on.
      */}
      <ClaimOutcomeModal />
      <Toaster position="bottom-right" />
      </TourProvider>
      </HelpProvider>
    </AssistantProvider>
  )
}
